'use client';

import { c as _c } from "react/compiler-runtime";
import React, { useCallback, useEffect, useState } from 'react';
import { ZoomableMermaid } from './zoomable-mermaid';
type MermaidWithFallbackProps = {
  content: string;
  className?: string;
  children?: React.ReactNode; // This will contain the caption/sources
  fallback?: React.ReactNode;
};

/**
 * Wrapper component that renders a Mermaid diagram and hides everything
 */
export function MermaidWithFallback(t0) {
  const $ = _c(12);
  const {
    content,
    className,
    children,
    fallback: t1
  } = t0;
  const fallback = t1 === undefined ? null : t1;
  const [hasError, setHasError] = useState(false);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setHasError(false);
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== content) {
    t3 = [content];
    $[1] = content;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  useEffect(t2, t3);
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      setHasError(true);
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const handleError = t4;
  if (hasError) {
    let t5;
    if ($[4] !== fallback) {
      t5 = <>{fallback}</>;
      $[4] = fallback;
      $[5] = t5;
    } else {
      t5 = $[5];
    }
    return t5;
  }
  let t5;
  if ($[6] !== content) {
    t5 = <ErrorBoundary onError={handleError}><ZoomableMermaid content={content} className="max-w-full overflow-x-auto" onError={handleError} hideOnError={true} /></ErrorBoundary>;
    $[6] = content;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const t6 = !hasError && children;
  let t7;
  if ($[8] !== className || $[9] !== t5 || $[10] !== t6) {
    t7 = <div className={className}>{t5}{t6}</div>;
    $[8] = className;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  return t7;
}

// Error boundary to catch any React errors during rendering
class ErrorBoundary extends React.Component<{
  children: React.ReactNode;
  onError: () => void;
}, {
  hasError: boolean;
}> {
  constructor(props: {
    children: React.ReactNode;
    onError: () => void;
  }) {
    super(props);
    this.state = {
      hasError: false
    };
  }
  static getDerivedStateFromError() {
    return {
      hasError: true
    };
  }
  componentDidCatch() {
    this.props.onError();
  }
  render() {
    if (this.state.hasError) {
      return null;
    }
    return this.props.children;
  }
}